
import React from 'react';
import SEO from '../components/SEO';
import { PRICING_PLANS, DEPLOYMENT_MODELS, MODULES } from '../constants';
import GlowCard from '../components/GlowCard';
import { Link } from 'react-router';
import { trackEvent } from '../components/analytics';

const PricingPage: React.FC = () => {
  return (
    <div className="animate-fade-in-up">
      <SEO
        title="Aviation Platform Pricing and Deployment Options"
        description="Pricing and deployment options for aviation software, including SaaS, private cloud, and on-premise environments for authorities, ANSPs, airlines, and operators."
      />
      <section className="py-24 px-4 max-w-7xl mx-auto">
        <div className="text-center mb-24">
          <h1 className="text-5xl lg:text-8xl font-bold font-heading mb-8 text-white tracking-tighter">Aviation platform pricing and deployment options.</h1>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
            From regional airports to national authorities. AviaGov replaces high legacy licensing costs with a modern, integrated platform.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-32">
          {PRICING_PLANS.map((plan, i) => (
            <GlowCard key={i} className={i === 1 ? 'border-accent shadow-[0_0_50px_rgba(34,211,238,0.15)] scale-105 z-10' : ''}>
              <div className="text-left">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
                  {i === 1 && <span className="px-2 py-1 bg-accent text-primary text-[10px] font-bold rounded tracking-tighter">MOST POPULAR</span>}
                </div>
                <p className="text-slate-500 text-sm mb-8 h-12 leading-relaxed">{plan.description}</p>
                <div className="text-4xl font-bold text-white mb-10 tracking-tight">{plan.price}</div>
                <ul className="space-y-4 mb-12 text-sm">
                  {plan.features.map((f, idx) => (
                    <li key={idx} className="flex items-center text-slate-300">
                      <span className="text-accent mr-3">✓</span> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`block w-full text-center py-4 rounded-xl font-bold transition-all ${
                    i === 1 ? 'bg-accent text-primary shadow-lg hover:shadow-accent/20' : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
                  }`}
                  onClick={() => trackEvent('generate_lead', { form_id: `pricing_plan_${plan.name.toLowerCase().replace(/\s+/g, '_')}` })}
                >
                  Contact for Quote
                </Link>
              </div>
            </GlowCard>
          ))}
        </div>

        {/* Modular Flexibility Section */}
        <div className="py-24 px-8 bg-surface border border-white/5 rounded-[3rem] mb-32 text-center overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-full bg-accent/5 blur-3xl rounded-full -z-10"></div>
          <h2 className="text-4xl font-bold font-heading text-white mb-6">Modular Flexibility</h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-16">
            Only need one solution? Each of our core products is available as a standalone subscription. Start with what you need and expand as your authority grows.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {MODULES.map((module) => (
              <div key={module.id} className="p-6 bg-primary border border-white/10 rounded-2xl hover:border-accent/40 transition-colors">
                <div className="text-white font-bold mb-2">{module.name}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-4">Standalone License</div>
                <Link to="/contact" className="text-accent text-xs font-bold hover:underline" onClick={() => trackEvent('generate_lead', { form_id: `pricing_module_${module.id}_pricing`, module: module.id === 'pans-ops' ? 'pans_ops' : module.id })}>Get Pricing &rarr;</Link>
              </div>
            ))}
          </div>
          <div className="mt-12 inline-flex flex-col items-center gap-3 bg-accent/10 border border-accent/20 rounded-2xl px-8 py-6">
            <div className="text-xs text-accent font-bold uppercase tracking-widest">Add-On Module</div>
            <div className="text-white font-bold text-lg">FlyMate - Grounded Compliance Support</div>
            <p className="text-slate-400 text-sm max-w-xl">
              Optional paid add-on that supports SMS, LMS, eAIP, and PANS-OPS workflows while keeping all safety decisions under human responsibility.
            </p>
            <Link to="/modules/ai-assistant" className="text-accent text-sm font-bold hover:underline">
              View FlyMate Module &rarr;
            </Link>
          </div>
        </div>

        {/* Deployment Models */}
        <div className="mb-32">
          <h2 className="text-3xl font-bold font-heading mb-12 text-center text-white">Deployment Options</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {DEPLOYMENT_MODELS.map((model, i) => (
              <div key={i} className="p-10 bg-surface border border-white/5 rounded-3xl group hover:border-accent/40 transition-colors">
                <h4 className="text-xl font-bold text-white mb-4">{model.name}</h4>
                <p className="text-slate-400 text-sm mb-6 leading-relaxed">{model.desc}</p>
                <div className="text-xs font-bold text-accent uppercase tracking-widest">{model.bestFor}</div>
              </div>
            ))}
          </div>
        </div>

        {/* On-Prem Requirements */}
        <div className="p-12 lg:p-16 bg-slate-900 border border-white/5 rounded-[3rem] grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h3 className="text-3xl font-bold text-white mb-6">Security & Sovereignty</h3>
            <p className="text-slate-400 mb-8 leading-relaxed">
              For authorities with strict national data requirements, AviaGov supports self-hosted deployments on your secure internal infrastructure.
            </p>
            <ul className="space-y-3 text-sm text-slate-500 font-mono">
              <li>• OS: Ubuntu 20.04+ / RHEL 8+</li>
              <li>• DB: MongoDB 5.0+</li>
              <li>• Security: AES-256 / TLS 1.3</li>
              <li>• Isolation: Dedicated Schemas</li>
            </ul>
          </div>
          <div className="text-center p-12 bg-primary rounded-3xl border border-white/10">
             <div className="text-accent font-bold text-5xl mb-4">4-8 Weeks</div>
             <p className="text-slate-400 text-sm">Average On-Premise Deployment Time</p>
             <Link to="/contact" className="mt-8 inline-block text-accent font-bold hover:underline" onClick={() => trackEvent('generate_lead', { form_id: 'pricing_request_prerequisites' })}>Request Prerequisites &rarr;</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
